import { authHeaders } from './auth/headers';

// ---- HTTP primitives ----

/**
 * Carries the HTTP status alongside the server's `{ error }` message, so callers can
 * branch on it (503 = not configured, 409 = duplicate, 422 = unreadable) instead of
 * string-matching a formatted message. `.message` is the server's own text when it
 * sent one, so existing `e instanceof Error ? e.message : ...` callers read better
 * than they did before rather than breaking.
 */
export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

/** Build an AdminApiError from a failed response, preferring the server's `{ error }`. */
async function fail(method: string, path: string, res: Response): Promise<never> {
  const text = await res.text().catch(() => '');
  let message = '';
  try {
    message = (JSON.parse(text) as { error?: string }).error ?? '';
  } catch {
    /* not JSON — fall through to the raw body */
  }
  throw new AdminApiError(
    message || `${method} ${path} failed: ${res.status} ${text}`.trim(),
    res.status,
  );
}

async function get<T>(path: string): Promise<T> {
  const auth = await authHeaders();
  const res = await fetch(path, { headers: { Accept: 'application/json', ...auth } });
  if (!res.ok) await fail('GET', path, res);
  return res.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const auth = await authHeaders();
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...auth },
    body: JSON.stringify(body),
  });
  if (!res.ok) await fail('POST', path, res);
  return res.json() as Promise<T>;
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const auth = await authHeaders();
  const res = await fetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...auth },
    body: JSON.stringify(body),
  });
  if (!res.ok) await fail('PATCH', path, res);
  return res.json() as Promise<T>;
}

async function del(path: string): Promise<void> {
  const auth = await authHeaders();
  const res = await fetch(path, { method: 'DELETE', headers: auth });
  if (!res.ok) await fail('DELETE', path, res);
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
  const auth = await authHeaders();
  const res = await fetch(path, {
    method: 'POST',
    headers: { Accept: 'application/json', ...auth },
    body: form,
  });
  if (!res.ok) await fail('POST', path, res);
  return res.json() as Promise<T>;
}

// ---- Types ----

export interface AdminFamily {
  id: number;
  slug: string;
  name: string;
  sortOrder: number;
  layoutCount: number;
}

export interface AdminLayout {
  id: number;
  code: string;
  name: string | null;
  familyId: number;
  familySlug: string;
  familyName: string;
  widthInches: number | null;
  depthInches: number | null;
  totalPriceCents: number;
  isActive: boolean;
  sortOrder: number;
  bomCount: number;
  renderCount: number;
}

export interface AdminRender {
  id: number;
  type: string;            // master | environmental | dimensional
  idx: number | null;      // 1..5 for environmental, else null
  blobPath: string;
  thumbBlobPath: string | null;
  url: string | null;
  thumbUrl: string | null;
  width: number | null;
  height: number | null;
  createdAt: string;
}

export interface AdminProduct {
  id: number;
  sku: string;
  name: string;
  unitPriceCents: number;
  usageCount: number;
}

export interface AdminBomItem {
  id: number;
  productId: number;
  sku: string;
  name: string;
  quantity: number;
  unitPriceCents: number;
  lineTotalCents: number;
  sortOrder: number;
}

export interface AdminSnapshot {
  id: number;
  effectiveDate: string;
  source: string;
  notes: string | null;
  createdAt: string;
}

export interface AdminUserRole {
  oid: string | null;
  name: string | null;
  email: string | null;
  lastSeen: string | null;
  signedIn: boolean;
  role: 'user' | 'design' | 'admin';
  roleIdentifier: string | null; // pass to revoke; null when role is default 'user'
  assignedBy: string | null;
  assignedByName: string | null;
  assignedByEmail: string | null;
  assignedAt: string | null;
}

export interface AdminShare {
  id: number;
  tokenPreview: string;
  createdBy: string | null;
  createdByName: string | null;
  createdByEmail: string | null;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  status: 'active' | 'expired' | 'revoked';
}

// ---- Families ----

export const listAdminFamilies = () => get<{ families: AdminFamily[] }>('/api/admin/families');
export const createFamily = (body: { slug: string; name: string; sort_order?: number }) =>
  post<{ family: AdminFamily }>('/api/admin/families', body);
export const updateFamily = (id: number, body: { name?: string; sort_order?: number }) =>
  patch<{ family: AdminFamily }>(`/api/admin/families/${id}`, body);
export const deleteFamily = (id: number) => del(`/api/admin/families/${id}`);

// ---- Layouts ----

export const listAdminLayouts = () => get<{ layouts: AdminLayout[] }>('/api/admin/layouts');
export const createLayout = (body: {
  family_id: number;
  code: string;
  name?: string;
  width_inches?: number;
  depth_inches?: number;
  sort_order?: number;
  is_active?: number;
}) => post<{ layout: AdminLayout }>('/api/admin/layouts', body);
export const updateLayout = (
  id: number,
  body: Partial<{
    family_id: number;
    name: string | null;
    width_inches: number | null;
    depth_inches: number | null;
    is_active: boolean;
    sort_order: number;
  }>,
) => patch<{ layout: AdminLayout }>(`/api/admin/layouts/${id}`, body);
export const deleteLayout = (id: number) => del(`/api/admin/layouts/${id}`);

// ---- Catalog page import ----

export interface ExtractedBomItem {
  sku: string;
  description: string;
  quantity: number | null;
  unitPriceCents: number | null;
  warnings: string[];
}

export interface ExtractedLayout {
  code: string;
  categoryRaw: string;
  familyId: number | null;
  familyName: string | null;
  widthInches: number | null;
  depthInches: number | null;
  /** Grand total printed on the page, for reconciliation against the computed total. */
  printedTotalCents: number | null;
  computedTotalCents: number;
  items: ExtractedBomItem[];
  warnings: string[];
}

/** Is page extraction configured server-side? Drives whether the paste step is offered. */
export const getExtractionAvailability = () =>
  get<{ available: boolean }>('/api/admin/layouts/extract');

/** Read a pasted screenshot / uploaded PDF of a catalog page. Writes nothing. */
export const extractLayoutsFromPage = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return postForm<{ candidates: ExtractedLayout[]; warnings: string[] }>(
    '/api/admin/layouts/extract',
    form,
  );
};

/** Commit a reviewed page: creates the layout and its BOM, auto-creating unknown SKUs. */
export const importLayout = (body: {
  family_id: number;
  code: string;
  name?: string;
  width_inches?: number;
  depth_inches?: number;
  items: { sku: string; description?: string; quantity: number; unit_price_cents?: number }[];
}) =>
  post<{ layout: AdminLayout; item_count: number; total_price_cents: number }>(
    '/api/admin/layouts/import',
    body,
  );

// ---- Renders ----

export const listRenders = (layoutId: number) =>
  get<{ renders: AdminRender[] }>(`/api/admin/renders/${layoutId}`);
export const uploadRender = (
  layoutId: number,
  file: File,
  type: string,
  index?: number,
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('type', type);
  if (index !== undefined) form.append('index', String(index));
  return postForm<{ render: AdminRender }>(`/api/admin/renders/${layoutId}`, form);
};
export const deleteRender = (renderId: number) => del(`/api/admin/renders/${renderId}`);

// ---- Products ----

export const listAdminProducts = () => get<{ products: AdminProduct[] }>('/api/admin/products');
export const createProduct = (body: { sku: string; name: string; unit_price_cents: number }) =>
  post<{ product: AdminProduct }>('/api/admin/products', body);
export const updateProduct = (id: number, body: { name?: string; unit_price_cents?: number }) =>
  patch<{ product: AdminProduct }>(`/api/admin/products/${id}`, body);
export const deleteProduct = (id: number) => del(`/api/admin/products/${id}`);

// ---- BOM ----

export const listBom = (layoutId: number) =>
  get<{ items: AdminBomItem[] }>(`/api/admin/bom/${layoutId}`);
export const addBomItem = (
  layoutId: number,
  body: { product_id: number; quantity: number; sort_order?: number },
) => post<{ item: AdminBomItem }>(`/api/admin/bom/${layoutId}`, body);
export const updateBomItem = (
  layoutId: number,
  productId: number,
  body: { quantity?: number; sort_order?: number },
) => patch<{ item: AdminBomItem }>(`/api/admin/bom/${layoutId}/${productId}`, body);
export const deleteBomItem = (layoutId: number, productId: number) =>
  del(`/api/admin/bom/${layoutId}/${productId}`);

// ---- Snapshots ----

export const listSnapshots = () => get<{ snapshots: AdminSnapshot[] }>('/api/admin/snapshots');
export const createSnapshot = (body: {
  effective_date: string;
  source: string;
  notes?: string;
}) => post<{ snapshot: AdminSnapshot }>('/api/admin/snapshots', body);
export const activateSnapshot = (id: number) =>
  post<{ layoutsUpdated: number; productsUpdated: number }>(
    `/api/admin/snapshots/${id}/activate`,
    {},
  );

// ---- Users ----

export const listUserRoles = () => get<{ users: AdminUserRole[] }>('/api/admin/users');
export const assignUserRole = (body: { identifier: string; role: 'admin' | 'design' }) =>
  post<{ user: AdminUserRole }>('/api/admin/users', body);
export const revokeUserRole = (oid: string) => del(`/api/admin/users/${encodeURIComponent(oid)}`);

// ---- Shares ----

export const listAdminShares = () => get<{ shares: AdminShare[] }>('/api/admin/shares');
export const revokeShare = (id: number) =>
  post<{ ok: boolean }>(`/api/admin/shares/${id}/revoke`, {});

// ---- API Keys ----

export interface AdminApiKey {
  id: number;
  name: string;
  keyPrefix: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export const listApiKeys = () => get<{ apiKeys: AdminApiKey[] }>('/api/admin/api-keys');
export const generateApiKey = (body: { name: string }) =>
  post<{ apiKey: AdminApiKey; rawKey: string }>('/api/admin/api-keys/generate', body);
export const revokeApiKey = (id: number) => del(`/api/admin/api-keys/${id}`);

// ---- Classic catalog documents ----

export interface ClassicTierStatus {
  pricing: boolean;
  available: boolean;
  url: string | null; // opaque /download/<token> when available
  originalName: string | null;
  uploadedAt: string | null;
}

export interface CatalogDocumentsAdminStatus {
  classic: { priced: ClassicTierStatus; unpriced: ClassicTierStatus };
}

export const fetchAdminCatalogDocuments = () =>
  get<CatalogDocumentsAdminStatus>('/api/admin/catalog-documents');

export const uploadClassicDocument = (pricing: boolean, file: File) => {
  const form = new FormData();
  form.append('pricing', String(pricing));
  form.append('file', file);
  return postForm<ClassicTierStatus>('/api/admin/catalog-documents', form);
};

export const deleteClassicDocument = async (pricing: boolean): Promise<ClassicTierStatus> => {
  const auth = await authHeaders();
  const path = `/api/admin/catalog-documents/${pricing ? 'priced' : 'unpriced'}`;
  const res = await fetch(path, { method: 'DELETE', headers: { Accept: 'application/json', ...auth } });
  if (!res.ok) await fail('DELETE', path, res);
  return res.json() as Promise<ClassicTierStatus>;
};

// ---- Layout source files (Product Visualizer integration) ----

export type LayoutSourceKind = 'dimensional-pdf' | 'isometric';

export interface LayoutSourceFile {
  url: string | null; // SAS blob URL when Azure is configured; null in unconfigured local dev
  hash: string; // SHA-256 of the stored bytes — the Visualizer's change signal
}

export interface LayoutSourceFilesStatus {
  dimensionalPdf: LayoutSourceFile | null;
  isometric: LayoutSourceFile | null;
}

export const fetchLayoutSourceFiles = (layoutId: number) =>
  get<LayoutSourceFilesStatus>(`/api/admin/layouts/${layoutId}/source-files`);

export const uploadLayoutSourceFile = (layoutId: number, kind: LayoutSourceKind, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return postForm<{ kind: LayoutSourceKind } & LayoutSourceFile>(
    `/api/admin/layouts/${layoutId}/source-files?kind=${kind}`,
    form,
  );
};

export const deleteLayoutSourceFile = (layoutId: number, kind: LayoutSourceKind) =>
  del(`/api/admin/layouts/${layoutId}/source-files?kind=${kind}`);
